import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CmsService } from '../../../core/services/cms.service';
import {
  Language,
  TranslationService,
} from '../../../core/services/translation.service';
import { EnquiryFormComponent } from '../enquiry-form/enquiry-form.component';

interface ProductCategory {
  id: number;
  name: string;
  description: string;
  products: Product[];
  viewAllLink: string;
}

interface Product {
  id: number;
  name: string;
  description: string;
  image: string;
  slug: string;
}

interface HomeModel {
  id: number;
  name: string;
  description: string;
  image: string;
  slug: string;
  features: string[];
  specifications: {
    totalArea: number;
    enclosedArea: number;
    bedrooms: number;
    bathrooms: number;
  };
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, EnquiryFormComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  isTransparent = false;
  isMobileMenuOpen = false;
  showEnquiryModal = false;
  currentLanguage: Language;
  languages: { code: Language; name: string }[] = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'hu', name: 'Magyar' },
  ];

  productCategories: ProductCategory[] = [];
  homeModels: HomeModel[] = [];
  productsLoading = true;
  homesLoading = true;

  private languageSubscription: Subscription | null = null;

  constructor(
    private translationService: TranslationService,
    private router: Router,
    private cmsService: CmsService,
    private renderer: Renderer2,
    private el: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    this.currentLanguage = this.translationService.getCurrentLanguage();

    // Set isTransparent based on the current route
    this.updateTransparency(this.router.url);

    // Listen for route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateTransparency(event.url);
      });
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  private loadInitialData(): void {
    this.loadProductCategories();
    this.loadHomeModels();

    this.languageSubscription =
      this.translationService.currentLanguage$.subscribe((language) => {
        this.currentLanguage = language;
        this.loadProductCategories();
        this.loadHomeModels();
        this.cdr.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    this.setupEnquiryModal();
  }

  /**
   * Set up the enquiry modal functionality
   */
  setupEnquiryModal(): void {
    const modalBlur = this.el.nativeElement.querySelector('.modal-blur');
    const modalContainer =
      this.el.nativeElement.querySelector('.modal-container');
    const closeModalBtn = this.el.nativeElement.querySelector(
      '#close-contact-modal'
    );
    const enquireButton = this.el.nativeElement.querySelector(
      'button[onclick="openEnquiryModal()"]'
    );

    if (modalBlur && modalContainer && closeModalBtn) {
      // Set up click event for the close button
      this.renderer.listen(closeModalBtn, 'click', () => {
        this.renderer.setStyle(modalBlur, 'opacity', '0%');
        this.renderer.setStyle(
          modalContainer,
          'transform',
          'translateX(-100%)'
        );
        setTimeout(() => {
          this.renderer.setStyle(modalBlur, 'display', 'none');
        }, 500);
        document.body.style.overflow = 'auto'; // Re-enable scrolling
      });

      // Set up click event for the modal blur (background)
      this.renderer.listen(modalBlur, 'click', () => {
        this.renderer.setStyle(modalBlur, 'opacity', '0%');
        this.renderer.setStyle(
          modalContainer,
          'transform',
          'translateX(-100%)'
        );
        setTimeout(() => {
          this.renderer.setStyle(modalBlur, 'display', 'none');
        }, 500);
        document.body.style.overflow = 'auto'; // Re-enable scrolling
      });
    }
  }

  /**
   * Load product categories from the CMS
   */
  loadProductCategories(): void {
    // If blogOnly is enabled, always use fallback data for products
    if (environment.blogOnly) {
      console.log('Using fallback product data (blogOnly mode enabled)');
      this.setupProductsFallbackData();
      this.productsLoading = false;
      return;
    }

    this.cmsService.getProductCategories().subscribe({
      next: (data) => {
        this.productCategories = data;
        this.productsLoading = false;
      },
      error: (error) => {
        console.error('Error loading product categories:', error);
        this.productsLoading = false;

        // Fallback data for development
        this.setupProductsFallbackData();
      },
    });
  }

  /**
   * Load home models from the CMS
   */
  loadHomeModels(): void {
    // If blogOnly is enabled, always use fallback data for home models
    if (environment.blogOnly) {
      console.log('Using fallback home models data (blogOnly mode enabled)');
      this.setupHomesFallbackData();
      this.homesLoading = false;
      return;
    }

    this.cmsService.getHomeModels().subscribe({
      next: (data) => {
        this.homeModels = data;
        this.homesLoading = false;
      },
      error: (error) => {
        console.error('Error loading home models:', error);
        this.homesLoading = false;

        // Fallback data for development
        this.setupHomesFallbackData();
      },
    });
  }

  /**
   * Setup fallback product data if CMS data fails to load
   */
  setupProductsFallbackData(): void {
    this.productCategories = [
      {
        id: 1,
        name: 'products.categories.garages.name',
        description: 'products.categories.garages.description',
        viewAllLink: '/products/garages',
        products: [
          {
            id: 1,
            name: this.translate('products.garages.garage6x3.name'),
            description: this.translate(
              'products.garages.garage6x3.description'
            ),
            image: '/assets/images/garages/6x3/1.jpeg',
            slug: 'garage-6x3',
          },
          {
            id: 2,
            name: this.translate('products.garages.garage6x6.name'),
            description: this.translate(
              'products.garages.garage6x6.description'
            ),
            image: '/assets/images/garage_with_carport_product_photo.jpg',
            slug: 'garage-6x6',
          },
        ],
      },
      {
        id: 2,
        name: 'products.categories.carports.name',
        description: 'products.categories.carports.description',
        viewAllLink: '/products/carports',
        products: [
          {
            id: 3,
            name: this.translate('products.carports.carport6x3.name'),
            description: this.translate(
              'products.carports.carport6x3.description'
            ),
            image: '/assets/images/carports/6x3/1.jpg',
            slug: 'carport-6x3',
          },
          {
            id: 4,
            name: this.translate('products.carports.carport6x6.name'),
            description: this.translate(
              'products.carports.carport6x6.description'
            ),
            image: '/assets/images/carports/6x6/1.jpg',
            slug: 'carport-6x6',
          },
        ],
      },
      {
        id: 3,
        name: 'products.categories.containers.name',
        description: 'products.categories.containers.description',
        viewAllLink: '/products/containers',
        products: [
          {
            id: 4,
            name: this.translate('products.containers.basic.name'),
            description: this.translate(
              'products.containers.basic.description'
            ),
            image: '/assets/images/container_product_photo.jpg',
            slug: 'basic',
          },
          {
            id: 5,
            name: this.translate('products.containers.premium.name'),
            description: this.translate(
              'products.containers.premium.description'
            ),
            image: '/assets/images/canopy_container_product_photo.jpg',
            slug: 'premium',
          },
        ],
      },
      {
        id: 3,
        name: 'products.categories.outdoorKitchens.name',
        description: 'products.categories.outdoorKitchens.description',
        viewAllLink: '/products/outdoor-kitchens',
        products: [
          {
            id: 6,
            name: this.translate('products.outdoorKitchens.basic.name'),
            description: this.translate(
              'products.outdoorKitchens.basic.description'
            ),
            image: '/assets/images/summer_kitchen_product_photo.jpg',
            slug: 'basic',
          },
          {
            id: 7,
            name: this.translate('products.outdoorKitchens.premium.name'),
            description: this.translate(
              'products.outdoorKitchens.premium.description'
            ),
            image: '/assets/images/summer_kitchen_product_photo.jpg',
            slug: 'premium',
          },
        ],
      },
      {
        id: 4,
        name: 'products.categories.garbageBins.name',
        description: 'products.categories.garbageBins.description',
        viewAllLink: '/products/garbage-bins',
        products: [
          {
            id: 8,
            name: this.translate('products.garbageBins.basic.name'),
            description: this.translate(
              'products.garbageBins.basic.description'
            ),
            image: '/assets/images/garbage-bins/basic/1.png',
            slug: 'basic',
          },
          {
            id: 9,
            name: this.translate('products.garbageBins.premium.name'),
            description: this.translate(
              'products.garbageBins.premium.description'
            ),
            image: '/assets/images/garbage-bins/premium/1.png',
            slug: 'premium',
          },
        ],
      },
    ];
  }

  /**
   * Setup fallback home models data if CMS data fails to load
   */
  setupHomesFallbackData(): void {
    this.homeModels = [
      {
        id: 1,
        name: 'homes.models.naturaMini.name',
        description: 'homes.models.naturaMini.description',
        image: '/assets/images/natura_mini/1_1_1.png',
        slug: 'natura-mini',
        features: [
          this.translate('homes.features.energyEfficient'),
          this.translate('homes.features.sustainableMaterials'),
          this.translate('homes.features.smartHome'),
          this.translate('homes.features.openFloorPlan'),
        ],
        specifications: {
          totalArea: 24,
          enclosedArea: 24,
          bedrooms: 2,
          bathrooms: 1,
        },
      },
      {
        id: 2,
        name: 'homes.models.naturaMaxi.name',
        description: 'homes.models.naturaMaxi.description',
        image: '/assets/images/natura_maxi/1_1_1.png',
        slug: 'natura-maxi',
        features: [
          this.translate('homes.features.energyEfficient'),
          this.translate('homes.features.sustainableMaterials'),
          this.translate('homes.features.smartHome'),
          this.translate('homes.features.familySized'),
          this.translate('homes.features.additionalStorage'),
        ],
        specifications: {
          totalArea: 32,
          enclosedArea: 32,
          bedrooms: 3,
          bathrooms: 2,
        },
      },
      {
        id: 3,
        name: 'homes.models.sensaMini.name',
        description: 'homes.models.sensaMini.description',
        image: '/assets/images/sensa_mini/1.png',
        slug: 'sensa-mini',
        features: [
          this.translate('homes.features.floorToCeiling'),
          this.translate('homes.features.openConcept'),
          this.translate('homes.features.luxuryFinishes'),
          this.translate('homes.features.indoorOutdoor'),
        ],
        specifications: {
          totalArea: 24,
          enclosedArea: 24,
          bedrooms: 2,
          bathrooms: 2,
        },
      },
      {
        id: 4,
        name: 'homes.models.sensaMaxi.name',
        description: 'homes.models.sensaMaxi.description',
        image: '/assets/images/sensa_maxi/sensa_1.png',
        slug: 'sensa-maxi',
        features: [
          this.translate('homes.features.floorToCeiling'),
          this.translate('homes.features.premiumFinishes'),
          this.translate('homes.features.spaciousMaster'),
          this.translate('homes.features.multipleLiving'),
          this.translate('homes.features.homeOffice'),
        ],
        specifications: {
          totalArea: 32,
          enclosedArea: 32,
          bedrooms: 3,
          bathrooms: 2,
        },
      },
    ];
  }

  /**
   * Update the transparency of the navbar based on the current route
   * @param url The current URL
   */
  private updateTransparency(url: string): void {
    // Only make the navbar transparent on the home page
    this.isTransparent = url === '/' || url === '';
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    // Only apply scroll-based transparency on the home page
    if (this.router.url === '/' || this.router.url === '') {
      this.isTransparent = window.scrollY <= 200;
    } else {
      this.isTransparent = false;
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    document.body.style.overflow = this.isMobileMenuOpen ? 'hidden' : 'auto';

    // Force non-transparent header when mobile menu is open
    if (this.isMobileMenuOpen) {
      this.isTransparent = false;
    } else {
      // When closing the mobile menu, update transparency based on current route and scroll position
      const isHomePage = this.router.url === '/' || this.router.url === '';
      if (isHomePage && window.scrollY <= 200) {
        this.isTransparent = true;
      } else {
        this.isTransparent = false;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
      this.languageSubscription = null;
    }
  }

  setLanguage(language: Language): void {
    this.translationService.setLanguage(language);
    this.cdr.detectChanges();
  }

  /**
   * Translate a key using the translation service
   * @param key The translation key to translate
   * @returns The translated string
   */
  translate(key: string): string {
    return this.translationService.translate(key);
  }

  /**
   * Generate a fragment identifier for a category to use in hash navigation
   * @param category The product category
   * @returns A sanitized fragment string
   */
  getCategoryFragment(category: ProductCategory): string {
    // Extract the category name from the viewAllLink or use the name directly
    const linkParts = category.viewAllLink.split('/');
    const categorySlug = linkParts[linkParts.length - 1];

    // Return the slug or a sanitized version of the name if no slug is available
    return categorySlug || category.name.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Navigate to a product category by scrolling to its section
   * @param category The product category to navigate to
   * @param event The click event
   */
  navigateToCategory(category: ProductCategory, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }

    // First update the URL with the fragment
    const fragment = this.getCategoryFragment(category);
    this.router.navigate(['/products'], { fragment });

    // Then manually scroll to the element (for cases when we're already on the products page)
    setTimeout(() => {
      const element = document.getElementById(fragment);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  /**
   * Open the enquiry modal
   */
  openEnquiryModal(): void {
    const modalBlur = this.el.nativeElement.querySelector('.modal-blur');
    const modalContainer =
      this.el.nativeElement.querySelector('.modal-container');

    if (modalBlur && modalContainer) {
      this.renderer.setStyle(modalBlur, 'display', 'block');
      setTimeout(() => {
        this.renderer.setStyle(modalBlur, 'opacity', '100%');
        this.renderer.setStyle(modalContainer, 'transform', 'translateX(0%)');
      }, 10);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    }
  }

  /**
   * Close the enquiry modal
   */
  closeEnquiryModal(): void {
    const modalBlur = this.el.nativeElement.querySelector('.modal-blur');
    const modalContainer =
      this.el.nativeElement.querySelector('.modal-container');

    if (modalBlur && modalContainer) {
      this.renderer.setStyle(modalBlur, 'opacity', '0%');
      this.renderer.setStyle(modalContainer, 'transform', 'translateX(-100%)');
      setTimeout(() => {
        this.renderer.setStyle(modalBlur, 'display', 'none');
      }, 500);
      document.body.style.overflow = 'auto'; // Re-enable scrolling
    }
  }
}
